import { useMemo } from "react";
import { MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fmtInt, fmtPct } from "@/lib/format";

// Normaliza uma localidade: usa a primeira parte relevante (ex: "Vila Madalena, 500" -> "Vila Madalena")
const bucket = (s) => {
  if (!s) return "";
  const first = String(s).split(",")[0].trim();
  return first || String(s).trim();
};

export default function HeatmapLocalidade({ prospectos = [] }) {
  const data = useMemo(() => {
    const counter = new Map();
    for (const p of prospectos) {
      const k = bucket(p.localidade);
      if (!k) continue;
      counter.set(k, (counter.get(k) || 0) + 1);
    }
    return [...counter.entries()].sort((a, b) => b[1] - a[1]);
  }, [prospectos]);

  const total = data.reduce((s, [, n]) => s + n, 0);
  const max = data[0]?.[1] || 1;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="heatmap-localidade">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Regiões mais frequentes</h3>
        </div>
        <Badge variant="outline">Guia expansão de cobertura</Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Onde estão concentrados os prospectos sem viabilidade. Priorize as regiões com mais demanda.
      </p>

      {data.length === 0 && (
        <div className="text-sm text-muted-foreground">Nenhuma localidade registrada ainda.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
        {data.slice(0, 10).map(([loc, count]) => {
          const p = (count / max) * 100;
          const intensity = Math.min(100, 20 + (count / max) * 80);
          return (
            <div key={loc} className="space-y-1" data-testid={`heatmap-item-${loc}`}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: `hsl(var(--primary) / ${intensity}%)` }}
                  />
                  <span className="truncate">{loc}</span>
                </div>
                <span className="tabular-nums text-xs text-muted-foreground">
                  {fmtInt(count)} · {fmtPct((count / total) * 100)}
                </span>
              </div>
              <Progress value={p} className="h-1.5" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
